import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
  AlertTriangleIcon,
  ChevronLeftIcon,
  CircleAlertIcon,
  LinkIcon,
  PhoneIcon,
  PlusIcon,
  Trash2Icon,
  ZapIcon,
} from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { DeleteConfirmPopoverContent } from '@/components/atoms/DeleteConfirmPopover/DeleteConfirmPopoverContent';
import Whatsapp from '@/components/atoms/Icons/Whatsapp';
import Link from '@/components/atoms/Link/Link';
import QrCodeGenerator from '@/components/atoms/QrCodeGenerator/QrCodeGenerator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import useDesks from '@/hooks/useDesks';
import { useIntegrations } from '@/hooks/useIntegrations';
import { TestNumberWithDeskId, useWhatsappTesting } from '@/hooks/useWhatsappTesting';
import { cn } from '@/lib/utils';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { ApiError } from '@/shared/types/api';
import { AccordionType, WhatsAppIntegration } from '@/shared/types/integration';
import { WHATSAPP_BRAIN_TEST_URL } from '@/util/constants';
import { whatsappTestNumber } from '@/util/validation/rules/integration-rules';

type ModalWhatsAppTestProps = { brainId: string };

type AddNumberForm = { label: string; phone_number: string; desk_id: string };

const ModalWhatsAppTest = ({ brainId }: ModalWhatsAppTestProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);

  const {
    modalState,
    selectedNumber,
    phoneNumbers,
    brainName,
    hasPermission,
    onNumberCreate,
    onNumberCreateError,
    onNumberSelect,
    onNumberDelete,
    onAddNumberClick,
    onBackClick,
  } = useWhatsappTesting(brainId);

  // Form state for adding a new number
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<AddNumberForm>();

  const desk_id = useWatch({ control, name: 'desk_id' });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numberToDelete, setNumberToDelete] = useState<TestNumberWithDeskId | null>(null);
  const { desk, desks, listStatus } = useDesks(desk_id);
  const integration_id = desk?.integrations?.find((int) => int.type === 'whatsapp')?.integration_id;
  const { integration } = useIntegrations<WhatsAppIntegration>(desk_id, integration_id);

  const showWarning = integration && integration?.config?.test_numbers?.length >= 4;

  const environmentOptions = useMemo(
    () =>
      desks
        ?.map((d) => ({
          value: d.desk_id,
          label: d.name,
          integration_id: d.integrations.find((int) => int.type === 'whatsapp')?.integration_id,
        }))
        ?.filter((opt) => !!opt.integration_id) ?? [],
    [desks]
  );

  // Auto-select environment if there's only one option
  useEffect(() => {
    if (environmentOptions.length === 1 && !desk_id) {
      setValue('desk_id', environmentOptions[0].value);
    }
  }, [environmentOptions, desk_id, setValue]);

  const onNumberAdd = useCallback(
    async (testNumber: AddNumberForm) => {
      setIsSubmitting(true);
      try {
        await onNumberCreate(
          { ...testNumber, integration_id } as TestNumberWithDeskId,
          integration
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [integration, integration_id, onNumberCreate]
  );

  useEffect(() => {
    if (!onNumberCreateError) {
      return;
    }
    const err = onNumberCreateError as unknown as ApiError;
    if (err.statusCode === 409) {
      setError(
        'phone_number',
        { type: 'custom', message: t('integrations.whatsapp.testing.num_conflict_error') },
        { shouldFocus: true }
      );
      return;
    }
    toast.error(onNumberCreateError.message);
  }, [onNumberCreateError, setError, t]);

  // QR code image
  const svgString = useMemo(() => encodeURIComponent(renderToStaticMarkup(<Whatsapp />)), []);
  const qrImage = `data:image/svg+xml,${svgString}`;

  const handleClose = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatch(popModal());
      }
    },
    [dispatch]
  );

  const getTitle = () => {
    switch (modalState) {
      case 'pending':
        return '';
      case 'selecting':
        return t('whatsapp_test.select_num.title');
      case 'creating':
        return t('whatsapp_test.add_num.title');
      case 'testing':
        return t('whatsapp_test.scan.title');
    }
  };

  const getSubtitle = () => {
    if (modalState === 'selecting') {
      return t('whatsapp_test.select_num.subtitle');
    }
    return undefined;
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="gap-4 sm:max-w-xl">
        {modalState !== 'pending' && (
          <DialogHeader>
            <DialogTitle>{getTitle()}</DialogTitle>
            {getSubtitle() && <DialogDescription>{getSubtitle()}</DialogDescription>}
          </DialogHeader>
        )}

        {/* Loading state */}
        {modalState === 'pending' && (
          <div className="flex h-75 items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}

        {/* Select number state */}
        {modalState === 'selecting' && (
          <div className="space-y-3">
            <Button size="sm" variant="ghost" disabled={!hasPermission} onClick={onAddNumberClick}>
              <PlusIcon size={16} className={hasPermission ? 'text-foreground-muted' : undefined} />
              {t('integrations.whatsapp.testing.add_num')}
            </Button>
            <div className="grid max-h-50 grid-cols-2 gap-3 overflow-y-auto p-1">
              {phoneNumbers.map((pn) => (
                <button
                  key={`phone-tile-${pn.phone_number}`}
                  className={cn(
                    'group relative flex h-19 flex-row items-center gap-3 rounded-lg border border-stroke bg-background p-4 text-left',
                    'transition-colors hover:bg-bg-muted-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    !hasPermission &&
                      !pn.rule_id &&
                      'cursor-not-allowed bg-muted/50 hover:bg-muted/50'
                  )}
                  onClick={() => onNumberSelect(pn)}
                  disabled={!hasPermission && !pn.rule_id}
                >
                  {hasPermission && (
                    <Popover
                      open={numberToDelete?.phone_number === pn.phone_number}
                      onOpenChange={(open) => {
                        if (!open) {
                          setNumberToDelete(null);
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost-neutral"
                          size="icon-xss"
                          className="absolute top-1.5 right-1.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:text-destructive hover:[&_svg]:text-destructive"
                          aria-label={t('common.delete')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setNumberToDelete(pn);
                          }}
                        >
                          <Trash2Icon size={14} />
                        </Button>
                      </PopoverTrigger>
                      <DeleteConfirmPopoverContent
                        side="top"
                        title={t('whatsapp_test.delete_num.title')}
                        description={t('whatsapp_test.delete_num.description')}
                        onCancel={() => setNumberToDelete(null)}
                        onConfirm={() => {
                          onNumberDelete(pn);
                          setNumberToDelete(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popover>
                  )}
                  <div className="flex min-w-0 flex-col items-start gap-1">
                    <p className="m-0 truncate text-sm leading-5 font-semibold text-foreground">
                      {pn.label}
                    </p>
                    <p className="m-0 truncate text-sm leading-5 font-normal text-foreground-muted">
                      +{pn.phone_number}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add number state */}
        {modalState === 'creating' && (
          <div className="space-y-4">
            {/* Name field */}
            <div className="space-y-2">
              <Label htmlFor="label">{t('integrations.whatsapp.testing.name')}</Label>
              <Controller
                name="label"
                control={control}
                rules={whatsappTestNumber().label}
                render={({ field }) => (
                  <Input
                    id="label"
                    placeholder={t('integrations.whatsapp.testing.name')}
                    {...field}
                    value={field.value ?? ''}
                    aria-invalid={!!errors.label}
                  />
                )}
              />
              {errors.label && <p className="text-xs text-destructive">{errors.label.message}</p>}
            </div>

            {/* Phone number field */}
            <div className="space-y-2">
              <Label htmlFor="phone_number">{t('integrations.whatsapp.testing.phone_num')}</Label>
              <Controller
                name="phone_number"
                control={control}
                rules={whatsappTestNumber().phone_number}
                render={({ field }) => (
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    aria-invalid={!!errors.phone_number}
                  />
                )}
              />
              {errors.phone_number && (
                <p className="text-xs text-destructive">{errors.phone_number.message}</p>
              )}
            </div>

            {/* Environment field - only show if multiple options */}
            {environmentOptions.length > 1 && (
              <div className="space-y-2">
                <Label>{t('environments.environment')}</Label>
                <Select value={desk_id} onValueChange={(value) => setValue('desk_id', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('try_it.select_environment')} />
                  </SelectTrigger>
                  <SelectContent>
                    {environmentOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showWarning && (
              <Alert variant="destructive">
                <AlertTriangleIcon size={16} />
                <AlertDescription>
                  <p>
                    <Trans
                      i18nKey={'whatsapp_test.add_num.warning'}
                      components={{
                        link1: (
                          <Link
                            href={`/${slug}/environments/${desk_id}/integrations/${integration_id}`}
                            onClick={handleClose}
                          />
                        ),
                      }}
                    />
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Scan and chat state */}
        {modalState === 'testing' && selectedNumber && (
          <div className="space-y-4">
            <Button
              variant="link"
              className="flex items-center bg-transparent p-0 text-foreground-muted"
              onClick={onBackClick}
            >
              <ChevronLeftIcon size={16} className="text-foreground-muted" />
              {t('common.back')}
            </Button>

            {!hasPermission && (
              <Alert variant="warning">
                <CircleAlertIcon size={16} />
                <AlertDescription>{t('whatsapp_test.scan.notice')}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-8">
              {/* Left panel - QR Code */}
              <div className="flex flex-col">
                <p className="mb-3 text-sm text-foreground-muted">
                  {t('whatsapp_test.scan.text1')}
                </p>
                <div className="h-45 w-45 rounded-lg border border-border p-3">
                  <QrCodeGenerator
                    overlayType="link"
                    url={WHATSAPP_BRAIN_TEST_URL}
                    image={qrImage}
                    height={156}
                    width={156}
                    margin={0}
                  />
                </div>
                <p className="mt-3 text-sm text-foreground-muted">
                  {t('whatsapp_test.scan.text2')}
                </p>
              </div>

              {/* Right panel - Connection info */}
              <div className="flex flex-1 flex-col items-center justify-center">
                {/* Phone number card */}
                <div className="w-full rounded-lg border border-border bg-background p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex size-8 items-center justify-center rounded-full bg-muted">
                        <PhoneIcon className="size-4 text-foreground-muted" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{selectedNumber.label}</p>
                        <p className="text-xs text-foreground-muted">
                          +{selectedNumber.phone_number}
                        </p>
                      </div>
                    </div>
                    <Link
                      className="text-xs"
                      onClick={() => {
                        navigate(
                          `/${slug}/environments/${selectedNumber.desk_id}/integrations/${selectedNumber.integration_id}#${AccordionType.WHATSAPP_TESTING}`
                        );
                        handleClose();
                      }}
                    >
                      {t('common.edit')}
                    </Link>
                  </div>
                </div>

                {/* Connection line */}
                <div className="flex h-12 flex-col items-center justify-center">
                  <div className="h-3 w-px border-l border-dashed border-foreground-muted/40" />
                  <div className="flex size-6 items-center justify-center rounded-full bg-primary">
                    <LinkIcon className="size-3 text-primary-foreground" />
                  </div>
                  <div className="h-3 w-px border-l border-dashed border-foreground-muted/40" />
                </div>

                {/* AI Agent card */}
                <div className="w-full rounded-lg border border-border bg-background p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-8 items-center justify-center rounded-full bg-muted">
                      <ZapIcon className="size-4 text-foreground-muted" />
                    </div>
                    <p className="text-sm font-medium">{brainName}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {modalState === 'creating' && (
          <DialogFooter>
            <Button
              onClick={handleSubmit(onNumberAdd)}
              disabled={listStatus === 'pending' || showWarning || !integration_id || !desk_id}
              isLoading={isSubmitting}
            >
              {t('whatsapp_test.add_num.add_number')}
            </Button>
          </DialogFooter>
        )}

        {modalState === 'testing' && (
          <DialogFooter>
            <Button onClick={handleClose}>{t('common.done')}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(ModalWhatsAppTest);
